import React from 'react';
import PropTypes from 'prop-types';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Avatar,
  Badge,
  Menu,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications,
  ExitToApp,
  PublicOutlined,
  PersonOutline,
  LockOutlined,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { usersAPI, authAPI, notificationsAPI } from '../../services/api';
import { getNotificationCategoryMeta } from '../../utils/notificationCategoryMeta';
import { getAdminUser, setAdminUser as persistAdminUser, clearAdminSession } from '../../utils/authSessionStorage';
import { formatRoleDisplay } from '../../utils/roleUtils';
import browserGlobal from '../../utils/browserGlobal';

const LANG_TEXT = '#374151';

/** Map stored role label to API role string (same semantics as Users.js). */
const roleToApi = (role) => {
  const raw = String(role || '').trim();
  const lower = raw.toLowerCase().replace(/\s+/g, '');
  if (lower === 'admin') return 'admin';
  if (lower === 'adminuser') return 'adminuser';
  if (lower === 'customer') return 'customer';
  if (lower === 'superadmin') return 'superadmin';
  if (lower === 'user') return 'user';
  if (raw === 'Admin') return 'admin';
  if (raw === 'Admin User') return 'adminuser';
  if (raw === 'Customer') return 'customer';
  if (raw === 'Super Admin') return 'superadmin';
  return 'user';
};

const readAdminUser = () => getAdminUser();

const langToggleActiveSx = (theme) => ({
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  border: `1px solid ${theme.palette.primary.main}`,
  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}`,
  '&:hover': {
    bgcolor: 'primary.dark',
    borderColor: 'primary.dark',
    boxShadow: `0 3px 12px ${alpha(theme.palette.primary.dark, 0.45)}`,
  },
});

const langToggleInactiveSx = (theme) => ({
  bgcolor: theme.palette.background.paper,
  color: theme.palette.text.secondary,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
  boxShadow: 'none',
  '&:hover': {
    bgcolor: alpha(theme.palette.primary.main, 0.06),
    borderColor: theme.palette.primary.light,
  },
});

/** Language pills — active uses theme primary; inactive uses subtle primary border */
const langToggleButtonSx = (active) => (theme) => ({
  flex: 1,
  py: 0,
  px: 2,
  minHeight: 40,
  borderRadius: '10px',
  fontWeight: 700,
  fontSize: '0.875rem',
  letterSpacing: '0.01em',
  textTransform: 'none',
  minWidth: 0,
  transition: 'background-color 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
  ...(active ? langToggleActiveSx(theme) : langToggleInactiveSx(theme)),
});

const formatRelativeTime = (dateString) => {
  if (!dateString) return '-';
  const createdAt = new Date(dateString);
  const diffMs = Date.now() - createdAt.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return createdAt.toLocaleDateString();
};

const NotificationItem = ({ item, onClick }) => {
  const meta = getNotificationCategoryMeta(item);
  const CategoryIcon = meta.icon;
  return (
    <MenuItem sx={{ px: 1.5, py: 0.75 }} onClick={() => onClick(item)}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.25,
          p: 1.25,
          width: '100%',
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: alpha('#1976d2', 0.04),
            borderColor: 'divider',
          },
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha('#000', 0.02),
            color: meta.color,
            flexShrink: 0,
          }}
        >
          <CategoryIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography
              variant="subtitle2"
              className="notification-title"
              sx={{ fontWeight: 700, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {item.title}
            </Typography>
            {!item.isRead && (
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  flexShrink: 0,
                }}
              />
            )}
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.25,
              display: '-webkit-box',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.35,
            }}
          >
            {item.body}
          </Typography>
          <Typography variant="caption" className="notification-time" sx={{ color: 'text.disabled', mt: 0.5, display: 'block' }}>
            {formatRelativeTime(item.createdAtUtc)}
          </Typography>
        </Box>
      </Box>
    </MenuItem>
  );
};

NotificationItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    description: PropTypes.string,
    body: PropTypes.string,
    time: PropTypes.string,
    createdAtUtc: PropTypes.string,
    type: PropTypes.string,
    category: PropTypes.string,
    isRead: PropTypes.bool,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

const dialogPaperSx = {
  borderRadius: '14px',
  overflow: 'hidden',
  border: '1px solid',
  borderColor: 'rgba(0,0,0,0.06)',
  boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
};

const saveButtonSx = {
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 700,
  px: 2.5,
  background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
  '&:hover': {
    background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
  },
};

const Header = ({ onToggleSidebar }) => {
  const { locale, setLocale, t } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const hideNotificationsUi = location.pathname === '/notifications';
  const locationRef = React.useRef(location);
  locationRef.current = location;
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [notificationsAnchor, setNotificationsAnchor] = React.useState(null);
  const [adminUser, setAdminUserState] = React.useState(readAdminUser);
  const [notifications, setNotifications] = React.useState([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [notificationLoading, setNotificationLoading] = React.useState(false);
  const [notificationActionLoading, setNotificationActionLoading] = React.useState(false);

  const [nameDialogOpen, setNameDialogOpen] = React.useState(false);
  const [nameForm, setNameForm] = React.useState({ firstName: '', lastName: '' });
  const [nameError, setNameError] = React.useState('');
  const [nameSaving, setNameSaving] = React.useState(false);

  const [passwordDialogOpen, setPasswordDialogOpen] = React.useState(false);
  const [passwordForm, setPasswordForm] = React.useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordError, setPasswordError] = React.useState('');
  const [passwordSaving, setPasswordSaving] = React.useState(false);
  const [showPasswords, setShowPasswords] = React.useState(false);

  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });

  const normalizeRole = (role) => {
    return formatRoleDisplay(role);
  };

  const displayName =
    [adminUser.firstName, adminUser.lastName].filter(Boolean).join(' ').trim() || t('common.adminUser');
  const displayEmail = adminUser.email || '';
  const displayRole = normalizeRole(adminUser.role);
  const avatarLetter = (adminUser.firstName || 'A').charAt(0).toUpperCase();

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationsOpen = (event) => {
    setNotificationsAnchor(event.currentTarget);
    fetchNotifications();
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchor(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    clearAdminSession();
    browserGlobal.location.href = '/login';
  };

  const fetchNotifications = React.useCallback(async (page = 1, pageSize = 20, silent = false) => {
    if (!silent) {
      setNotificationLoading(true);
    }
    try {
      const res = await notificationsAPI.getNotifications(page, pageSize, locale);
      const payload = res?.data?.payload || {};
      const list = payload?.notifications?.items || [];
      setNotifications(Array.isArray(list) ? list : []);
      setUnreadCount(Number(payload?.unreadCount || 0));
    } catch (err) {
      if (!silent) {
        setSnackbar({
          open: true,
          severity: 'error',
          message: err?.response?.data?.errorMessage || err?.message || 'Failed to load notifications',
        });
      }
    } finally {
      if (!silent) {
        setNotificationLoading(false);
      }
    }
  }, [locale]);

  React.useEffect(() => {
    if (hideNotificationsUi) return;
    fetchNotifications();
  }, [fetchNotifications, hideNotificationsUi]);

  React.useEffect(() => {
    const intervalId = browserGlobal.setInterval(() => {
      if (locationRef.current.pathname === '/notifications') return;
      fetchNotifications(1, 20, true);
    }, 15000);

    return () => {
      browserGlobal.clearInterval(intervalId);
    };
  }, [fetchNotifications]);

  React.useEffect(() => {
    if (hideNotificationsUi) {
      setNotificationsAnchor(null);
    }
  }, [hideNotificationsUi]);

  const handleMarkSingleRead = async (item) => {
    if (!item || item.isRead) return;
    setNotifications((prev) => prev.map((n) => (n.id === item.id ? { ...n, isRead: true } : n)));
    setUnreadCount((prev) => Math.max(0, prev - 1));
    try {
      await notificationsAPI.markAsRead(item.id);
    } catch (err) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: err?.response?.data?.errorMessage || err?.message || 'Failed to mark notification read',
      });
      fetchNotifications();
    }
  };

  const handleMarkAllRead = async () => {
    setNotificationActionLoading(true);
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications((prev) => prev.map((item) => ({ ...item, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: err?.response?.data?.errorMessage || err?.message || 'Failed to mark all notifications read',
      });
    } finally {
      setNotificationActionLoading(false);
    }
  };

  const handleViewAllNotifications = () => {
    handleNotificationsClose();
    navigate('/notifications');
  };

  const openNameDialog = () => {
    handleProfileMenuClose();
    const u = readAdminUser();
    setNameForm({
      firstName: u.firstName || '',
      lastName: u.lastName || '',
    });
    setNameError('');
    setNameDialogOpen(true);
  };

  const openPasswordDialog = () => {
    handleProfileMenuClose();
    setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError('');
    setPasswordDialogOpen(true);
  };

  const handleSaveName = async () => {
    const fn = nameForm.firstName.trim();
    const ln = nameForm.lastName.trim();
    if (!fn && !ln) {
      setNameError(t('header.nameRequired'));
      return;
    }
    setNameSaving(true);
    setNameError('');
    try {
      const res = await authAPI.updateDisplayName({
        firstName: fn,
        lastName: ln,
      });
      if (res?.data && res.data.success === false) {
        throw new Error(res.data.message || res.data.errorMessage || t('header.updateFailed'));
      }
      const next = { ...readAdminUser(), ...adminUser, firstName: fn, lastName: ln };
      persistAdminUser(next);
      setAdminUserState(next);
      setSnackbar({ open: true, severity: 'success', message: t('header.nameUpdated') });
      setNameDialogOpen(false);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.errorMessage ||
        err?.message ||
        t('header.updateFailed');
      setNameError(msg);
    } finally {
      setNameSaving(false);
    }
  };

  const handleSavePassword = async () => {
    const { oldPassword, newPassword, confirmPassword } = passwordForm;
    if (!oldPassword || !newPassword || !confirmPassword) {
      setPasswordError(t('header.fillAllPassword'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(t('header.passwordMismatch'));
      return;
    }
    setPasswordSaving(true);
    setPasswordError('');
    try {
      const res = await authAPI.changePassword({
        oldPassword,
        newPassword,
        confirmPassword,
      });
      if (res?.data && res.data.success === false) {
        throw new Error(res.data.message || res.data.errorMessage || t('header.updateFailed'));
      }
      setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordDialogOpen(false);
      setSnackbar({ open: true, severity: 'success', message: t('header.passwordUpdated') });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.errorMessage ||
        err?.message ||
        t('header.updateFailed');
      setPasswordError(msg);
    } finally {
      setPasswordSaving(false);
    }
  };

  const passwordAdornment = (
    <InputAdornment position="end">
      <IconButton
        aria-label="toggle password visibility"
        onClick={() => setShowPasswords((v) => !v)}
        edge="end"
        size="small"
      >
        {showPasswords ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  const renderNotificationMenuBody = () => {
    if (notificationLoading) {
      return (
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={20} />
        </Box>
      );
    }
    if (notifications.length === 0) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No notifications found.
          </Typography>
        </Box>
      );
    }
    return notifications.map((item) => (
      <NotificationItem key={item.id} item={item} onClick={handleMarkSingleRead} />
    ));
  };

  return (
    <AppBar position="fixed">
      <Toolbar
        sx={{
          minHeight: { xs: 60, md: 64 },
          px: { xs: 1, sm: 2 },
        }}
      >
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          onClick={onToggleSidebar}
          sx={{ mr: 2, ml: { xs: 0, sm: 0 } }}
        >
          <MenuIcon />
        </IconButton>

        <Typography
          variant="h6"
          component="div"
          sx={{
            flexGrow: 1,
            fontWeight: 600,
            color: '#ffffff',
            letterSpacing: '0.02em',
            fontSize: { xs: '1rem', sm: '1.25rem' },
            pr: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {isMobile ? 'Welivin ' : t('app.title')}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {displayRole && (
            <Chip
              label={displayRole}
              size="small"
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.18)',
                color: '#ffffff',
                fontWeight: 600,
                fontSize: '0.75rem',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                height: 26,
              }}
            />
          )}

          {!hideNotificationsUi && (
            <IconButton color="inherit" onClick={handleNotificationsOpen} aria-label={t('header.notifications')}>
              <Badge badgeContent={unreadCount} color="error" max={99}>
                <Notifications />
              </Badge>
            </IconButton>
          )}

          <IconButton color="inherit" onClick={handleProfileMenuOpen}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'primary.main',
                fontSize: '0.875rem',
              }}
            >
              {avatarLetter}
            </Avatar>
          </IconButton>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          slotProps={{
            paper: {
              elevation: 0,
              sx: {
                overflow: 'hidden',
                mt: 1.5,
                width: 300,
                maxWidth: 'calc(100vw - 24px)',
                borderRadius: '12px',
                border: '1px solid',
                borderColor: 'rgba(0,0,0,0.06)',
                boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1.5,
                  gap: 1.5,
                  fontSize: '0.875rem',
                  transition: (theme) =>
                    theme.transitions.create(['background-color', 'color'], {
                      duration: 200,
                    }),
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'white',
                    '& .MuiSvgIcon-root': {
                      color: 'white',
                    },
                  },
                },
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{ p: 2, pb: 1.75, bgcolor: '#fafbfc' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: 'primary.main',
                  fontSize: '1.25rem',
                  boxShadow: '0 2px 8px rgba(124, 58, 237, 0.25)',
                }}
              >
                {avatarLetter}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: LANG_TEXT, lineHeight: 1.3 }} noWrap>
                  {displayName}
                </Typography>
                {displayEmail && (
                  <Typography variant="caption" display="block" sx={{ color: '#64748b', mt: 0.25 }} noWrap>
                    {t('header.email')}: {displayEmail}
                  </Typography>
                )}
                {displayRole && (
                  <Typography variant="caption" display="block" sx={{ color: '#64748b' }}>
                    {t('header.role')}: {displayRole}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />
          <MenuItem onClick={openNameDialog}>
            <PersonOutline fontSize="small" sx={{ color: '#64748b' }} />
            <Typography variant="body2" fontWeight={600} sx={{ color: LANG_TEXT }}>
              {t('header.changeName')}
            </Typography>
          </MenuItem>
          <MenuItem onClick={openPasswordDialog}>
            <LockOutlined fontSize="small" sx={{ color: '#64748b' }} />
            <Typography variant="body2" fontWeight={600} sx={{ color: LANG_TEXT }}>
              {t('header.changePassword')}
            </Typography>
          </MenuItem>
          <Box sx={{ px: 2, pt: 1, pb: 2, bgcolor: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.75 }}>
              <PublicOutlined sx={{ fontSize: 20, color: '#64748b' }} />
              <Typography
                component="span"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.9375rem',
                  color: LANG_TEXT,
                  letterSpacing: '0.02em',
                }}
              >
                {t('header.language')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: '10px', alignItems: 'stretch' }}>
              <Button
                color="inherit"
                disableElevation
                disableRipple
                onClick={() => setLocale('en')}
                sx={langToggleButtonSx(locale === 'en')}
              >
                {t('header.lang.en')}
              </Button>
              <Button
                color="inherit"
                disableElevation
                disableRipple
                onClick={() => setLocale('de')}
                sx={langToggleButtonSx(locale === 'de')}
              >
                {t('header.lang.de')}
              </Button>
            </Box>
          </Box>
          <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />
          <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
            <ExitToApp fontSize="small" />
            <Box>
              <Typography variant="body2" fontWeight={500}>
                {t('header.logout')}
              </Typography>
              <Typography variant="caption">{t('header.signOut')}</Typography>
            </Box>
          </MenuItem>
        </Menu>

        {!hideNotificationsUi && (
          <Menu
            anchorEl={notificationsAnchor}
            open={Boolean(notificationsAnchor)}
            onClose={handleNotificationsClose}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  overflow: 'hidden',
                  filter: 'drop-shadow(0px 10px 24px rgba(0,0,0,0.16))',
                  mt: 1.5,
                  width: 430,
                  maxWidth: 'calc(100vw - 32px)',
                  borderRadius: '14px',
                  '& .MuiMenuItem-root': {
                    px: 0,
                    py: 0,
                  },
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box
              sx={{
                p: 2,
                pb: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                {t('header.notifications')}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'primary.main',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
                onClick={() => {
                  handleMarkAllRead();
                }}
              >
                {notificationActionLoading ? 'Please wait...' : t('header.markAllRead')}
              </Typography>
            </Box>
            <Box
              sx={{
                maxHeight: 420,
                overflowY: 'auto',
                p: 1,
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': { backgroundColor: 'grey.100' },
                '&::-webkit-scrollbar-thumb': { backgroundColor: 'grey.400', borderRadius: '4px' },
              }}
            >
              {renderNotificationMenuBody()}
            </Box>
            <Divider />
            <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button
                fullWidth
                size="small"
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 600,
                }}
                onClick={handleViewAllNotifications}
              >
                {t('header.viewAllNotifications')}
              </Button>
            </Box>
          </Menu>
        )}

        <Dialog
          open={nameDialogOpen}
          onClose={() => !nameSaving && setNameDialogOpen(false)}
          fullWidth
          maxWidth="sm"
          slotProps={{ paper: { sx: dialogPaperSx } }}
        >
          <DialogTitle sx={{ fontWeight: 800, pb: 1, pt: 2.5, px: 2.5 }}>
            {t('header.dialogChangeName')}
          </DialogTitle>
          <DialogContent sx={{ px: 2.5, pt: 0, pb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              {t('header.currentName')}
            </Typography>
            <Box
              sx={{
                mb: 2.5,
                p: 1.75,
                borderRadius: '10px',
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                {[adminUser.firstName, adminUser.lastName].filter(Boolean).join(' ').trim() ||
                  t('common.adminUser')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                fullWidth
                label={t('header.newFirstName')}
                value={nameForm.firstName}
                onChange={(e) => setNameForm((f) => ({ ...f, firstName: e.target.value }))}
                disabled={nameSaving}
                autoComplete="given-name"
              />
              <TextField
                fullWidth
                label={t('header.newLastName')}
                value={nameForm.lastName}
                onChange={(e) => setNameForm((f) => ({ ...f, lastName: e.target.value }))}
                disabled={nameSaving}
                autoComplete="family-name"
              />
            </Box>
            {nameError && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: '10px' }}>
                {nameError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 1, gap: 1 }}>
            <Button
              onClick={() => setNameDialogOpen(false)}
              disabled={nameSaving}
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
            >
              {t('header.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveName}
              disabled={nameSaving}
              sx={{ ...saveButtonSx, minWidth: 120 }}
            >
              {nameSaving ? <CircularProgress size={22} color="inherit" /> : t('header.save')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={passwordDialogOpen}
          onClose={() => !passwordSaving && setPasswordDialogOpen(false)}
          fullWidth
          maxWidth="sm"
          slotProps={{ paper: { sx: dialogPaperSx } }}
        >
          <DialogTitle sx={{ fontWeight: 800, pb: 1, pt: 2.5, px: 2.5 }}>
            {t('header.dialogChangePassword')}
          </DialogTitle>
          <DialogContent sx={{ px: 2.5, pt: 0, pb: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
              <TextField
                fullWidth
                type={showPasswords ? 'text' : 'password'}
                label={t('header.oldPassword')}
                value={passwordForm.oldPassword}
                onChange={(e) => setPasswordForm((f) => ({ ...f, oldPassword: e.target.value }))}
                disabled={passwordSaving}
                autoComplete="current-password"
                InputProps={{ endAdornment: passwordAdornment }}
              />
              <TextField
                fullWidth
                type={showPasswords ? 'text' : 'password'}
                label={t('header.newPassword')}
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm((f) => ({ ...f, newPassword: e.target.value }))}
                disabled={passwordSaving}
                autoComplete="new-password"
                InputProps={{ endAdornment: passwordAdornment }}
              />
              <TextField
                fullWidth
                type={showPasswords ? 'text' : 'password'}
                label={t('header.confirmPassword')}
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm((f) => ({ ...f, confirmPassword: e.target.value }))}
                disabled={passwordSaving}
                autoComplete="new-password"
                InputProps={{ endAdornment: passwordAdornment }}
              />
            </Box>
            {passwordError && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: '10px' }}>
                {passwordError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 1, gap: 1 }}>
            <Button
              onClick={() => setPasswordDialogOpen(false)}
              disabled={passwordSaving}
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
            >
              {t('header.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSavePassword}
              disabled={passwordSaving}
              sx={{ ...saveButtonSx, minWidth: 120 }}
            >
              {passwordSaving ? <CircularProgress size={22} color="inherit" /> : t('header.save')}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{
            top: { xs: '64px', sm: '72px' },
          }}
        >
          <Alert
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            severity={snackbar.severity}
            variant="standard"
            sx={{
              minWidth: { xs: 'calc(100vw - 24px)', sm: 360 },
              maxWidth: 460,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: snackbar.severity === 'success' ? '#b7ebd0' : '#f7c3c8',
              bgcolor: snackbar.severity === 'success' ? '#eaf8ef' : '#fdecef',
              color: snackbar.severity === 'success' ? '#0f5132' : '#842029',
              boxShadow: '0 8px 24px rgba(16, 24, 40, 0.12)',
              fontWeight: 500,
              '& .MuiAlert-icon': {
                color: snackbar.severity === 'success' ? '#1fb97a' : '#e35d6a',
                alignItems: 'center',
              },
              '& .MuiAlert-action': {
                alignItems: 'center',
                color: snackbar.severity === 'success' ? '#0f5132' : '#842029',
                mr: 0.25,
              },
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Toolbar>
    </AppBar>
  );
};

Header.propTypes = {
  onToggleSidebar: PropTypes.func.isRequired,
};

export default Header;
